<template>
  <div id="mux-video" :style="{ filter: blur ? 'blur(75px)' : '' }">
    <video
      ref="video"
      preload="auto"
      :loop="loop"
      playsinline
      :muted="isSpeaking"
      @timeupdate="onTimeUpdate"
      @ended="onEnded"
      @play="onPlay"
    >
      <source
        :src="`https://stream.mux.com/${muxId}.m3u8?max_resolution=720p`"
        type="application/x-mpegURL"
      />
    </video>
    <NuxtImg
      v-if="fallbackImage"
      v-show="loaded && !isPlaying"
      :src="fallbackImage"
      @load="loaded = true"
      :preload="preload"
      :loading="loading"
      :fetchPriority="fetchPriority"
      alt="Enterprise"
    />
  </div>
</template>
<script setup>
import logger from "debug";
import Hls from "hls.js";
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";

const debug = logger("travvir:MuxVideo.vue");
const props = defineProps({
  muxId: {
    type: String,
    default: "",
  },
  fallbackImage: {
    type: String,
    default: "",
  },
  blur: {
    type: Boolean,
    default: false,
  },
  isSpeaking: {
    type: Boolean,
    default: true,
  },
  pause: {
    type: Boolean,
    default: false,
  },
  loop: {
    type: Boolean,
    default: true,
  },
  fetchPriority: {
    type: String,
    default: "auto", // can be 'high', 'low', or 'auto'
  },
  preload: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: String,
    default: "eager",
  },
});

const emit = defineEmits(["timeupdate", "ended", "play", "isPlaying"]);

const video = ref(null);
const hls = ref(null);
const observer = ref(null);
const isPlaying = ref(false);
const isInViewport = ref(false);
const loaded = ref(false);

const initializeVideo = () => {
  const videoEl = video.value;
  const src = `https://stream.mux.com/${props.muxId}.m3u8?max_resolution=720p`;

  debug(videoEl?.canPlayType("application/vnd.apple.mpegurl"));

  if (videoEl?.canPlayType("application/vnd.apple.mpegurl")) {
    debug("setting up native hls");
    videoEl.src = src;
  } else {
    let hlsInstance = new Hls();
    hlsInstance.loadSource(src);
    hlsInstance.attachMedia(videoEl);
    hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
      debug("hls manifest parsed");
      hlsInstance.startLoad();
      debug("HLS started loading");
    });
    hls.value = hlsInstance;
  }

  // Video loads but remains paused on mount
  debug("Video initialized and paused on mount");
};

const setupIntersectionObserver = () => {
  // Clean up any existing observer
  destroyObserver();

  const videoEl = video.value;
  if (!videoEl) return;
  // Configure options based on device size
  const mediaQuery = "(min-width: 992px)";
  const options = window.matchMedia(mediaQuery).matches
    ? { rootMargin: "0px", threshold: 0.5 }
    : { rootMargin: "50px", threshold: 0.3 };

  // Create new observer
  observer.value = new IntersectionObserver((entries) => {
    const [entry] = entries;
    isInViewport.value = entry.isIntersecting;
    debug(
      `Video visibility changed: ${isInViewport.value ? "visible" : "hidden"}`
    );

    // Only control playback if not forced by pause prop
    if (!props.pause) {
      if (isInViewport.value) {
        attemptPlay();
      } else {
        console.warn("Video is out of viewport, pausing playback");
        pauseVideo();
      }
    }
  }, options);

  // Start observing
  observer.value.observe(videoEl);
  debug("Intersection Observer set up");
};

const destroyObserver = () => {
  if (observer.value) {
    observer.value.disconnect();
    observer.value = null;
    debug("Intersection Observer destroyed");
  }
};

const attemptPlay = () => {
  const videoEl = video.value;
  if (!videoEl || props.pause) return;

  videoEl
    .play()
    .then(() => {
      emit("isPlaying");
      debug("Video playback started");
      isPlaying.value = true;
    })
    .catch((error) => {
      debug("Error playing video:", error.message);
    });
};

const playVideo = () => {
  // Only play if in viewport and not paused by prop
  if (isInViewport.value && !props.pause) {
    attemptPlay();
  }
};

const pauseVideo = () => {
  const videoEl = video.value;
  console.warn("Pausing video", videoEl);
  if (videoEl && !videoEl.paused) {
    videoEl.pause();
  }
};

const restart = () => {
  const videoEl = video.value;
  if (videoEl) {
    videoEl.currentTime = 0;
    playVideo();
  }
};

const onTimeUpdate = () => {
  const videoEl = video.value;
  if (videoEl) {
    emit("timeupdate", videoEl.currentTime, videoEl.duration);
  }
};

const onEnded = () => {
  debug("Video ended");
  emit("ended");
};

const onPlay = () => {
  // isPlaying.value = true;
  debug("Video playing");
  emit("play");
};

onMounted(async () => {
  debug("muxvideo mounted");
  await nextTick();
  initializeVideo();
  setupIntersectionObserver();
});

onBeforeUnmount(() => {
  destroyObserver();
});

watch(
  () => props.pause,
  (newVal) => {
    if (newVal) {
      pauseVideo();
    } else if (isInViewport.value) {
      // Only play if in viewport
      attemptPlay();
    }
  },
  { immediate: true }
);

defineExpose({
  restart,
  playVideo,
  pauseVideo,
});
</script>
<style lang="scss" scoped>
video,
img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  // filter: blur(75px);
}
img {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}
#mux-video {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
